import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Constants } from '@praktio/common';

import { getSlide, initialize, initializeSlide } from '../../ducks/slidesLitmosReducer';
import { getCurrentSlide } from '../../utils/slide';

import { updateStatus as updateLearnerStatus } from '../../ducks/learnerReducer';
import { updatePlayer } from '../../ducks/explainerReducer';
import CustomNav from '../templates/CustomNav';
import { RootState } from '../../ducks/configureStore';
import ModuleTopBar from '../ModuleTopBar/ModuleTopBar';
import ModuleBottomBar from '../ModuleBottomBar/ModuleBottomBar';
import Slide from '../Slide/Slide';
import { MessageTypes, WindowSizes } from '../../utils/constants';
import type { ParentToLearnerMessage } from '@praktio/common-ui';
import { isSlideDataIncluded, notifyLoaded } from '../../utils/learnerUtils';

import './SlideContainer.scss';

const { TemplateTypes, CustomNavType } = Constants;

const IncludeStatus = true;
// Commented out for now while we figure out how to toggle this
// searchParams && searchParams.has('status') ? !!searchParams.get('status') : false;

const SlideIncluded = isSlideDataIncluded();

// Polling duration of 30 seconds: 30 * 1 second
const PollingDuration = 30 * 1000;

// Inactive duration of Ten minutes: 10 * 60 * 1 second
const InactiveDuration = 10 * 60 * 1000;

interface IProps {
  windowSize: WindowSizes;
}

let inLMS = false;
try {
  inLMS = document?.referrer?.startsWith('https://lms');
} catch (e) {
  console.error(e);
}

const SlideContainer = ({ windowSize }: IProps) => {
  const dispatch = useDispatch();

  // LOCAL STATE
  const [hidePlay, setHidePlay] = useState(false);
  const [tocIsOpen, setTocIsOpen] = useState(false);
  const [moduleNameOverride, setModuleNameOverride] = useState<string>();

  // REDUX STATE
  const {
    currentSlide,
    currentSlideNumber,
    totalSlideNumber,
    noAccess,
    learnerId,
    isPublic,
    moduleName,
    disableOverlay,
    loading,
    slideError,
  } = useSelector((state: RootState) => {
    const currentSlide = getCurrentSlide(state);
    const { number: currentSlideNumber = 0, total: totalSlideNumber = 0 } =
      currentSlide.learnerData || {};
    const { learnerId } = state.learner;
    const { type, _id } = currentSlide || {};
    const isCustomNavSlide = type === CustomNavType;

    // isPublic = embedded
    // if isPublic = no ToC Menu Button
    const isPublic: boolean = !!(
      learnerId &&
      learnerId.startsWith('_LUI-') &&
      (isCustomNavSlide || _id)
    );
    const moduleName: string = state.slidesLitmos?.data?.name ?? '';
    const disableOverlay = state.slidesLitmos?.data?.disableOverlay;
    return {
      currentSlide,
      currentSlideNumber,
      totalSlideNumber,
      settings: state.slidesLitmos.settings,
      noAccess: state.slidesLitmos.noAccess,
      learnerId,
      isPublic,
      moduleName,
      disableOverlay,
      loading: state.slidesLitmos.loading,
      slideError: state.slidesLitmos.slideError,
    };
  });

  // REFS
  const lastActive = useRef(new Date());
  const statusInterval: MutableRefObject<number | undefined> = useRef();
  const hasMounted = useRef(false);

  const inLitmos = !!(window as Win).GetStatus;

  // This useEffect is for initial load to tie into MessageEvent protocol
  useEffect(() => {
    const messageListener = (e: MessageEvent<any>) => {
      if (document.referrer && document.referrer.startsWith(e.origin)) {
        const messageData = e.data as ParentToLearnerMessage;
        switch (messageData.type) {
          case MessageTypes.Slide:
            if (messageData.data?._id) {
              dispatch(getSlide(messageData.data._id));
            }
            break;
          case MessageTypes.ToC:
            setTocIsOpen(!!messageData.data);
            break;
          case MessageTypes.Title:
            setModuleNameOverride(messageData.data);
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('message', messageListener);
    notifyLoaded();
    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [dispatch]);

  useEffect(() => {
    const activeStatus = (isStart: boolean) => {
      if (currentSlide._id && learnerId) {
        const now = new Date();
        const duration = +now - +lastActive.current;
        if (duration <= InactiveDuration) {
          dispatch(updateLearnerStatus(learnerId, currentSlide._id, lastActive.current, isStart));
        }
      }
    };

    if (!hasMounted.current) {
      hasMounted.current = true;
      if (IncludeStatus && currentSlide._id) {
        statusInterval.current = setInterval(activeStatus, PollingDuration);
      }

      try {
        if (!SlideIncluded) {
          dispatch(initialize());
        } else {
          dispatch(initializeSlide());
        }
      } catch (e) {
        console.error(e);
      }
    } else if (IncludeStatus && currentSlide._id) {
      clearInterval(statusInterval.current);
      statusInterval.current = setInterval(activeStatus, PollingDuration);
    } else if (IncludeStatus && statusInterval.current) {
      clearInterval(statusInterval.current);
    }

    return () => {
      if (IncludeStatus && currentSlide._id) {
        clearInterval(statusInterval.current);
      }
    };
  }, [currentSlide._id, dispatch, learnerId]);

  const isExplainer = currentSlide?.type === TemplateTypes.Explanation;
  const isFirstSlide = currentSlideNumber === 1;
  const isFirstPublicExplainer =
    isFirstSlide && isPublic && isExplainer && !disableOverlay && !hidePlay; // First slide is an explainer and the overlay is displaying

  const recordActivity = useCallback(() => {
    if (currentSlide._id && learnerId) {
      const now = new Date();
      const duration = +now - +(lastActive.current || now);
      lastActive.current = now;
      if (duration > InactiveDuration) {
        //activeStatus(true);
        dispatch(updateLearnerStatus(learnerId, currentSlide._id, lastActive.current, true));
      }
    }
  }, [learnerId, currentSlide._id, dispatch]);

  const renderSlide = () => {
    if (!currentSlide || !currentSlide.type) {
      return null;
    }

    switch (currentSlide.type) {
      case TemplateTypes.CheckAllApply:
      case TemplateTypes.Conclusions:
      case TemplateTypes.ClickBestSpot:
      case TemplateTypes.Explanation:
        return <Slide disableAutoPlay={isFirstPublicExplainer} windowSize={windowSize} />;
      case CustomNavType:
        return <CustomNav />;
      default:
        return <div>Unknown slide</div>;
    }
  };

  if (noAccess) {
    return (
      <div>
        <h2 className="invalid-browser-text">Module is not available.</h2>
      </div>
    );
  }

  const { _id, type } = currentSlide || {};
  const showSpinner = !inLMS && (loading || !type) && !slideError;
  const showSlideContent = !loading && !!type && !slideError;

  return (
    <div
      className={'preview-mode' + (inLitmos ? '-litmos' : '')}
      onMouseOver={recordActivity}
      onMouseMove={recordActivity}
      onKeyDown={recordActivity}
      onFocus={recordActivity}
    >
      <div className="slide-block">
        {showSpinner ? (
          <div className="overlay">
            <div className="overlay-spinner" />
          </div>
        ) : null}

        {slideError ? (
          <div className="slide-error" role="alert">
            <h2 className="slide-error-text">
              We couldn&apos;t load this slide. Please check your connection and try again.
            </h2>

            <button
              type="button"
              className="slide-error-retry"
              onClick={() => window.location.reload()}
            >
              Try again
            </button>
          </div>
        ) : null}

        {showSlideContent ? (
          <>
            <ModuleTopBar
              moduleName={moduleNameOverride || moduleName}
              currentSlideNumber={currentSlideNumber}
              totalSlideNumber={totalSlideNumber}
              padTitle={!isPublic && !tocIsOpen}
              windowSize={windowSize}
            />

            <div
              className={classNames('slide-container', {
                'h-100': !_id && type !== CustomNavType,
                'top-bar': isPublic,
              })}
            >
              {renderSlide()}
            </div>

            <ModuleBottomBar windowSize={windowSize} />
          </>
        ) : null}
      </div>
      {isPublic && !hidePlay && !disableOverlay && (
        <div
          className="overlay point"
          onClick={() => {
            setHidePlay(true);
            if (isExplainer) {
              dispatch(
                updatePlayer({
                  playing: true,
                }),
              );
            }
          }}
        >
          <i className="material-icons overlay-play">play_arrow</i>
        </div>
      )}
    </div>
  );
};

export default SlideContainer;
